import { License } from "src/assets/libs/office-open-xml-js/license";
import {
  ISource,
  IReference,
} from "src/assets/libs/office-open-xml-presentation-js/interfaces";
import { OpenDocumentHandler } from "./opendocument-handler";
import { v4 as UUIDv4 } from "uuid";
import { ICreateParagraphParameters } from "src/assets/libs/office-open-xml-js/interfaces";
import { IWordprocessingParagraphTextParameters } from "src/assets/libs/office-open-xml-wordprocessing-js/interfaces";
import { OfficeOpenXmlWordprocessingConfig } from "src/config/ooxml.config";
import { EImageRole } from "src/models/image-wrapper";
import { LicensesBaseHref } from "src/models/licenses";
export class OdtHandler extends OpenDocumentHandler {
  public async addLicensingElement(
    legalText: string,
    targetLicense: License
  ): Promise<void> {
    await this
      .addStyle(`<style:style style:name="convOERter_pagebreak" style:family="paragraph" style:parent-style-name="Standard">
        <style:paragraph-properties fo:break-before="page"/>
        <style:text-properties officeooo:rsid="001177ca" officeooo:paragraph-rsid="001177ca"/>
    </style:style>`);

    const _logoFileEnding: string = "png";
    const _ccLicenseImageAsBase64 = targetLicense.logo.getData();
    const imageFileName: string = `Pictures/CCLICENSE_${UUIDv4()}.${_logoFileEnding}`;
    // Format legal text to be displayed in multiple paragraph tags
    const legalTextSplit = legalText.split("\n");
    const formattedLegalText = legalTextSplit
      .map((paragraph) => `<text:p>${paragraph}</text:p>`)
      .join("\n");
    const page = `<text:p text:style-name="convOERter_pagebreak">
        <draw:frame draw:style-name="fr1" draw:name="Grafik 2" text:anchor-type="as-char" svg:width="4.1354in" svg:height="1.4591in" draw:z-index="5">
<draw:image xlink:href="${imageFileName}" xlink:type="simple" xlink:show="embed" xlink:actuate="onLoad" draw:mime-type="image/png"/>
</draw:frame>
        </text:p>
        ${formattedLegalText}`;
    await this.appendPage(page, "content.xml");
    // Add the CC license image file
    await this.addBase64Image({
      nameAndFullPath: imageFileName,
      imageAsBase64: _ccLicenseImageAsBase64,
    });
    // Add manifest/meta information for the CC license image
    // this is necessary, as otherwise the presentation is detected as being corrupted
    await this.addManifestForImage(imageFileName, "image/" + _logoFileEnding);
  }

  /**
   * Add Sources Slide to the presentation
   * @param sources The sources to add
   */
   public async addSources(sources: ISource[]): Promise<void> {
    let sourceItems: string = "";
    for (const source of sources) {
      const params: ICreateParagraphParameters<IWordprocessingParagraphTextParameters> =
        {
          texts: [
            {
              // eslint-disable-next-line max-len
              text: `${OfficeOpenXmlWordprocessingConfig.prefixRefAbbr}${source.refAbbreviation}${OfficeOpenXmlWordprocessingConfig.postfixRefAbbr}: `,
            },
          ],
        };

      switch (source.role) {
        case EImageRole.screenshot:
          const date = source.screenshotData.date;
          params.texts.push({
            text: `Screenshot of ${
              source.screenshotData.description
            }, taken on ${date.toLocaleDateString("en-US")}. `,
          });
          break;
        default:
          params.texts.push({
            text: `Image `,
          });
          break;
      }
      if (source.title) {
        params.texts.push({
          text: `"${source.title}" `,
        });
      }
      if (source.author) {
        params.texts.push({
          text: "by ",
        });
        if (source.authorUrl) {
          params.texts.push({
            text: source.author + " ",
            properties: {
              hyperlink: {
                url: source.authorUrl,
              },
            },
          });
        } else {
          params.texts.push({ text: source.author + " " });
        }
      }
      // license is always given
      params.texts.push(
        {
          text: `under the license `,
        },
        {
          text: `${source.licenseHumanReadable}`,
          properties: {
            hyperlink: {
              url:
                LicensesBaseHref[source.licenseHumanReadable],
            },
          },
        }
      );
      if (source.originName) {
        params.texts.push(
          {
            text: ` via `,
          },
          {
            text: source.originName,
            properties: {
              hyperlink: {
                url: source.originUrl,
              },
            },
          }
        );
      } else if (source.originUrl) {
        params.texts.push(
          {
            text: ` via `,
          },
          {
            text: source.originUrl,
            properties: {
              hyperlink: {
                url: source.originUrl,
              },
            },
          }
        );
      }
      // if image was edited, add info about it as seen on p. 117 of "Freie Unterrichtsmaterialien" by Jören Muuß-Merholz
      if (source.edited) {
        params.texts.push({
          text: `; Edited: ${source.edited.actions.join(", ")} by ${
            source.edited.editor
          }`,
        });
      }
      // Add the created paragraph node to the array
      let text = "";
      params.texts.forEach((p) => {
        if(p.properties && p.properties.hyperlink){
            text += `<text:a xlink:type="simple" xlink:href="${p.properties.hyperlink.url}">${p.text}</text:a>`;
        }else{
            text += p.text;
        }
      });
      sourceItems += `<text:list-item><text:p>${text}</text:p></text:list-item>`;
    }

    // eslint-disable-next-line max-len
    const sourcesPage = `<text:p text:style-name="convOERter_pagebreak"></text:p><text:list>${sourceItems}</text:list>>`;
    await this.appendPage(sourcesPage, "content.xml");
  }

   /**
   * Adds bib references (e.g. [1]) to images in the corresponding slides
   * They reference the source in the Source slide
   * @param references The references to add
   * @returns
   */
    public addBibRefsToImages(references: IReference[]): Promise<void> {
        // throw new Error('Method not implemented.');
        return new Promise<void>(async (resolve, reject) => {
          const _xml = await this._zip.file("content.xml").async("text");
          const _docAppXml: Document = await new DOMParser().parseFromString(
            _xml,
            "application/xml"
          );

          // First create the corresponding style in the xml file
          this.addStyle(`<style:style style:name="oer-converter-refs" style:family="graphic" style:parent-style-name="standard"><style:graphic-properties draw:stroke="none" svg:stroke-color="#000000" draw:fill="none" draw:fill-color="#ffffff" draw:auto-grow-height="true" draw:auto-grow-width="false" fo:max-height="0cm" fo:min-height="0cm" /><style:paragraph-properties style:writing-mode="lr-tb" /></style:style>`);
          const imageEls = _docAppXml.querySelectorAll("image");
          imageEls.forEach((img) => {
            const fileName = img.getAttribute("xlink:href");
            const ref = references.find((reference) => reference.file === fileName);
            if (ref) {
              // found reference to replaced image
              // insert bibref:

              // get position of image
              const x = img.parentElement.getAttribute("svg:x");
              const y = img.parentElement.getAttribute("svg:y");
              const height = img.parentElement.getAttribute("svg:height");
              const width = img.parentElement.getAttribute("svg:width");

              const refX = Number.parseFloat(x) + Number.parseFloat(width) + "cm";
              const refY = Number.parseFloat(y) + Number.parseFloat(height) + "cm";

              // eslint-disable-next-line max-len
              const completeXML = `<?xml version="1.0" encoding="UTF-8"?><office:document-content xmlns:officeooo="http://openoffice.org/2009/office" xmlns:anim="urn:oasis:names:tc:opendocument:xmlns:animation:1.0" xmlns:smil="urn:oasis:names:tc:opendocument:xmlns:smil-compatible:1.0" xmlns:presentation="urn:oasis:names:tc:opendocument:xmlns:presentation:1.0" xmlns:css3t="http://www.w3.org/TR/css3-text/" xmlns:grddl="http://www.w3.org/2003/g/data-view#" xmlns:xhtml="http://www.w3.org/1999/xhtml" xmlns:formx="urn:openoffice:names:experimental:ooxml-odf-interop:xmlns:form:1.0" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:rpt="http://openoffice.org/2005/report" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:chart="urn:oasis:names:tc:opendocument:xmlns:chart:1.0" xmlns:svg="urn:oasis:names:tc:opendocument:xmlns:svg-compatible:1.0" xmlns:draw="urn:oasis:names:tc:opendocument:xmlns:drawing:1.0" xmlns:text="urn:oasis:names:tc:opendocument:xmlns:text:1.0" xmlns:oooc="http://openoffice.org/2004/calc" xmlns:style="urn:oasis:names:tc:opendocument:xmlns:style:1.0" xmlns:ooow="http://openoffice.org/2004/writer" xmlns:meta="urn:oasis:names:tc:opendocument:xmlns:meta:1.0" xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:fo="urn:oasis:names:tc:opendocument:xmlns:xsl-fo-compatible:1.0" xmlns:ooo="http://openoffice.org/2004/office" xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0" xmlns:dr3d="urn:oasis:names:tc:opendocument:xmlns:dr3d:1.0" xmlns:table="urn:oasis:names:tc:opendocument:xmlns:table:1.0" xmlns:number="urn:oasis:names:tc:opendocument:xmlns:datastyle:1.0" xmlns:of="urn:oasis:names:tc:opendocument:xmlns:of:1.2" xmlns:calcext="urn:org:documentfoundation:names:experimental:calc:xmlns:calcext:1.0" xmlns:tableooo="http://openoffice.org/2009/table" xmlns:drawooo="http://openoffice.org/2010/draw" xmlns:loext="urn:org:documentfoundation:names:experimental:office:xmlns:loext:1.0" xmlns:dom="http://www.w3.org/2001/xml-events" xmlns:field="urn:openoffice:names:experimental:ooo-ms-interop:xmlns:field:1.0" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:math="http://www.w3.org/1998/Math/MathML" xmlns:form="urn:oasis:names:tc:opendocument:xmlns:form:1.0" xmlns:script="urn:oasis:names:tc:opendocument:xmlns:script:1.0" xmlns:xforms="http://www.w3.org/2002/xforms" office:version="1.3"><draw:frame draw:style-name="oer-converter-refs" draw:layer="layout" svg:width="1.3cm" svg:height="1cm" svg:x="${refX}" svg:y="${refY}"><draw:text-box><text:p>[${ref.refAbbreviation}]</text:p></draw:text-box></draw:frame></office:document-content>`;

              const domparser = new DOMParser();
              const bibRefDoc = domparser.parseFromString(
                completeXML,
                "application/xml"
              );
              const bibRefEl = bibRefDoc.querySelector("frame");
              img.parentElement.parentElement.appendChild(bibRefEl);
            }
          });
          const _xmlSerializer = new XMLSerializer();
          this._zip.file(
            "content.xml",
            _xmlSerializer.serializeToString(_docAppXml)
          );
          resolve();
        });
      }

  private async appendPage(page: string, xmlFileName: string) {
    const _xml = await this._zip.file(xmlFileName).async("text");
    const _docAppXml: Document = await new DOMParser().parseFromString(
      _xml,
      "application/xml"
    );
    _docAppXml.querySelector("text").innerHTML += page;
    const _xmlSerializer = new XMLSerializer();
    await this._zip.file(
      xmlFileName,
      _xmlSerializer.serializeToString(_docAppXml)
    );
  }

  private async addStyle(style: string) {
    const _xml = await this._zip.file("styles.xml").async("text");
    const _docAppXml: Document = await new DOMParser().parseFromString(
      _xml,
      "application/xml"
    );
    _docAppXml.querySelector("styles").innerHTML += style;
    const _xmlSerializer = new XMLSerializer();
    await this._zip.file(
      "styles.xml",
      _xmlSerializer.serializeToString(_docAppXml)
    );
  }
}
